import { useContext, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { useIntl } from 'react-intl';
import { AlertsContext } from '../../../contexts/AlertsContext';
import PromoCodeContext from '../../../contexts/PromoCodeContext';
import AuthService from '../../../services/auth/AuthService';
import { validateEmail } from '../../../utils';
import { QueryStringContext } from '../../../contexts/QueryStringContext';
import { QUERY_KEYS, QUERY_VALUES } from '../../../utils/constants/settings';

export default function useGetUserFormData() {
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    wantsEmails: true,
    wantsFree: false,
    mobile: '',
  });
  const [validForm, setValidForm] = useState(false);
  const intl = useIntl();
  const { showError } = useContext(AlertsContext);
  const { getQueryStringVal } = useContext(QueryStringContext);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isEmpty, setIsEmpty] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    mobile: false,
  });
  const inputValidator = (object) => {
    const { password, ...data } = object;
    const isValid = !Object.values(data).some((x) => (
      typeof x === 'string' ? !x.trim() : !x
    ));
    setValidForm(isValid);
  };

  const { PromoCode } = useContext(PromoCodeContext);

  const handleChange = (e) => {
    const { value, id } = e.target;
    const input = {
      ...user,
      [id]: value,
    };
    setUser(input);
  };

  const handleChangeNumbers = (e) => {
    const { value, id } = e.target;
    const regexNumbers = /^[0-9\b]+$/;
    if (value === '' || regexNumbers.test(value)) {
      const input = {
        ...user,
        [id]: value,
      };
      setUser(input);
    }
  };

  const validateFields = () => {
    const invalidFields = [];
    if (!user.firstName.trim()) {
      invalidFields.push('firstName');
    }
    if (!user.lastName.trim()) {
      invalidFields.push('lastName');
    }
    if (!validateEmail(user.email)) {
      invalidFields.push('email');
    }
    if (!isPasswordValid) {
      invalidFields.push('password');
    }
    if (user.mobile.trim() !== '') {
      const regex = /^[+]*[0-9]{1,13}$/g;
      const validate = user.mobile.match(regex);
      if (!validate) {
        invalidFields.push('mobile');
      }
      if (user.mobile.length < 10 || user.mobile.length > 13) {
        invalidFields.push('mobile');
      }
    }

    if (invalidFields[0]) {
      const newEmpty = {
        firstName: false,
        lastName: false,
        email: false,
        password: false,
        mobile: false,
      };
      invalidFields.forEach((i) => {
        newEmpty[i] = true;
      });
      setIsEmpty(newEmpty);

      const element = document.getElementById(invalidFields[0]);
      if (element) {
        window.scrollTo({
          behavior: 'smooth',
          top: element.offsetTop - 130,
        });
      }
    }

    return !invalidFields.length;
  };

  const signUp = async () => {
    if (!validateFields()) return;
    try {
      const promocode = PromoCode.isValid ? PromoCode.code : '';
      const plan = PromoCode.plans.find((item) => item.isMonthly);
      const typeOfTrial = getQueryStringVal(QUERY_KEYS.tt);
      const isOptIn = (typeOfTrial === QUERY_VALUES.tt);
      const signUpPayload = {
        ...user,
        language: intl.locale,
        promocode,
        currencyCode: plan.currencyCode,
        countryCode: plan.countryCode,
        isOptIn,
      };
      await AuthService.signup(signUpPayload);
    } catch (error) {
      Sentry.captureException(error);
      showError(intl.formatMessage({
        id: 'alert.error.signUpEmail',
        defaultMessage: 'There is already an account created with the email you have entered.',
      }));
      // eslint-disable-next-line consistent-return
      return false;
    }
  };

  return {
    user,
    setUser,
    validForm,
    setValidForm,
    isPasswordValid,
    setIsPasswordValid,
    inputValidator,
    handleChange,
    handleChangeNumbers,
    signUp,
    isEmpty,
  };
}
